import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FileUpload } from '../components/FileUpload';
import { SplitAndClassifyRequestForm, SplitAndClassifyRequestFormRef } from '../components/SplitAndClassifyRequestForm';
import { SplitAndClassifyResults } from '../components/SplitAndClassifyResults';
import { splitAndClassify, ping, ApiError, generateUploadUrl, uploadFileWithUrl } from '../api';
import { SplitAndClassifyRequest, SplitAndClassifyResponse } from '../types';
import { config } from '../config';

enum ProcessingStep {
  FILE_SELECTION = 'file_selection',
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ERROR = 'error'
}

const CACHE_KEY = 'formx-split-classify-form-data';

interface FormDataWithAuth extends Omit<SplitAndClassifyRequest, 'asset_id'> {
  auth_token: string;
}

export function SplitAndClassify() {
  // Load cached form data
  const loadCachedData = (): FormDataWithAuth => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedData = JSON.parse(cached);
        return {
          ...config.defaultFormValues,
          ...parsedData,
          // Ensure document_type_definitions has at least one entry
          document_type_definitions: parsedData.document_type_definitions?.length > 0 
            ? parsedData.document_type_definitions 
            : [{ name: '' }],
          auth_token: parsedData.auth_token || ''
        };
      }
    } catch (error) {
      console.warn('Failed to load cached form data:', error);
    }
    
    return {
      auth_token: '',
      llm_model_provider: config.defaultFormValues.llmModelProvider,
      llm_model_name: null,
      chat_complete_request_args: null,
      openrouter_api_key: null,
      endpoint: '',
      token: '',
      workspace_or_extractor_id: '',
      is_workspace: config.defaultFormValues.isWorkspace,
      pdf_dpi: config.defaultFormValues.pdfDpi,
      auto_adjust_image_size: config.defaultFormValues.autoAdjustImageSize,
      original_file_name: null,
      leading_line_limit: config.defaultFormValues.leadingLineLimit,
      trailing_line_limit: config.defaultFormValues.trailingLineLimit,
      document_type_definitions: [{ name: '' }],
      add_prompt: config.defaultFormValues.addPrompt,
    };
  };

  const [step, setStep] = useState<ProcessingStep>(ProcessingStep.FILE_SELECTION);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadSucceeded, setUploadSucceeded] = useState<boolean>(false);
  const [assetId, setAssetId] = useState<string>('');
  const [results, setResults] = useState<SplitAndClassifyResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataWithAuth>(loadCachedData);
  const [isConfigCollapsed, setIsConfigCollapsed] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const formRef = useRef<SplitAndClassifyRequestFormRef>(null);

  // Validation utility function for fallback when form is not mounted
  const validateFormData = useCallback((data: FormDataWithAuth): boolean => {
    return (
      data.endpoint.trim() !== '' &&
      data.token.trim() !== '' &&
      data.workspace_or_extractor_id.trim() !== '' &&
      data.document_type_definitions.some(def => def.name.trim() !== '')
    );
  }, []);

  // Initialize form validity based on cached data
  useEffect(() => {
    setIsFormValid(validateFormData(loadCachedData()));
  }, [validateFormData]);

  // Cache form data whenever it changes
  const handleFormDataChange = (newFormData: FormDataWithAuth) => {
    setFormData(newFormData);
    // Check form validity using ref if form is mounted, otherwise use callback validation
    const isValid = formRef.current ? formRef.current.isValid() : validateFormData(newFormData);
    setIsFormValid(isValid);
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(newFormData));
    } catch (error) {
      console.warn('Failed to cache form data:', error);
    }
  };

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handleCheckConnection = async () => {
    setIsCheckingConnection(true);
    try {
      await ping(); // Use empty auth token for connection check
      setIsConnected(true);
      toast.success('Successfully connected to backend');
    } catch (err) {
      setIsConnected(false);
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to connect to backend';
      toast.error(errorMessage);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    setSelectedFile(file);
    setUploadSucceeded(false);
    setAssetId(''); // Clear previous asset ID
    setError('');
    setStep(ProcessingStep.UPLOADING);

    try {
      const uploadUrlResponse = await generateUploadUrl(
        formData.auth_token,
        file.size,
        file.type,
      );
      await uploadFileWithUrl(uploadUrlResponse, file);
      setAssetId(uploadUrlResponse.asset_id);
      setUploadSucceeded(true);
      setStep(ProcessingStep.FILE_SELECTION);
      toast.success('File uploaded successfully');
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to upload file';
      setError(errorMessage);
      setStep(ProcessingStep.ERROR);
      toast.error(errorMessage);
      // Clear the selected file on upload failure to allow re-selection
      setSelectedFile(null);
    }
  };

  const handleFormSubmit = async (formData: Omit<SplitAndClassifyRequest, 'asset_id'>, formAuthToken: string) => {
    if (!assetId) {
      const errorMessage = 'No file uploaded';
      setError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setError('');
    setResults(null); // Clear previous results
    setStep(ProcessingStep.PROCESSING);

    try {
      const request: SplitAndClassifyRequest = {
        ...formData,
        asset_id: assetId,
        original_file_name: selectedFile?.name || null,
      };

      const response = await splitAndClassify(request, formAuthToken);
      setResults(response);
      setStep(ProcessingStep.COMPLETED);
      toast.success('Document processed successfully');
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? `${err.message}${err.detail ? `: ${JSON.stringify(err.detail)}` : ''}` 
        : 'Processing failed';
      setError(errorMessage);
      setStep(ProcessingStep.ERROR);
      toast.error(errorMessage);
    }
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadSucceeded(false);
    setAssetId('');
    setResults(null);
    setError('');
    setStep(ProcessingStep.FILE_SELECTION);
  };

  const isProcessing = step === ProcessingStep.UPLOADING || step === ProcessingStep.PROCESSING;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1>FormX Split & Classify</h1>
          {/* FIXME: refactor the navigation links implementation to make it scalable to future items */}
          <nav className="app-nav" aria-label="Main">
            <Link to="/bank-statement-extract" className="btn btn-nav" target="_blank" rel="noopener noreferrer">
              Bank Statement Extract
            </Link>
            <Link to="/split-and-classify" className="btn btn-nav active" aria-current="page">
              Split & Classify
            </Link>
          </nav>
        </div>
        <div className="connection-status">
          {isConnected === null && (
            <button 
              onClick={handleCheckConnection} 
              className="btn btn-secondary"
              disabled={isCheckingConnection}
            >
              {isCheckingConnection ? 'Checking...' : 'Check Connection'}
            </button>
          )}
          {isConnected === true && (
            <>
              <span className="status-connected">✓ Connected</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Recheck'}
              </button>
            </>
          )}
          {isConnected === false && (
            <>
              <span className="status-disconnected">✗ Backend unavailable</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Retry Connection'}
              </button>
            </>
          )}
        </div>
      </header>

      <main className="app-main">
        <div className="app-layout">
          <div className="left-panel">
            <div className="upload-section">
              <h2>Upload PDF File</h2>
              {selectedFile && uploadSucceeded ? (
                step === ProcessingStep.PROCESSING ? (
                  <div className="uploading-state">
                    <div className="upload-progress">
                      <span>Processing {selectedFile?.name}...</span>
                    </div>
                    <div className="upload-details">
                      Size: {selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : '0'} MB
                    </div>
                  </div>
                ) : (
                  <div className="file-selected">
                    <span className="file-info">
                      ✓ {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button onClick={handleReset} className="btn btn-secondary">
                      Choose Different File
                    </button>
                  </div>
                )
              ) : step === ProcessingStep.UPLOADING ? (
                <div className="uploading-state">
                  <div className="upload-progress">
                    <div className="loading-spinner"></div>
                    <span>Uploading {selectedFile?.name}...</span>
                  </div>
                  <div className="upload-details">
                    Size: {selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : '0'} MB
                  </div>
                </div>
              ) : (
                <FileUpload onFileSelect={handleFileSelect} disabled={isProcessing} />
              )}
              
              {/* Submit Button */}
              {uploadSucceeded && (
                <div className="submit-section">
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    disabled={!isFormValid || isProcessing}
                    onClick={() => {
                      const { auth_token, ...requestData } = formData;
                      const payload = {
                        ...requestData,
                        chat_complete_request_args: formData.chat_complete_request_args,
                      };
                      handleFormSubmit(payload, auth_token);
                    }}
                  >
                    Process Document
                  </button>
                </div>
              )}
            </div>

            <div className="config-section">
              <div 
                className="config-header" 
                onClick={() => setIsConfigCollapsed(!isConfigCollapsed)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsConfigCollapsed(!isConfigCollapsed);
                  }
                }}
                aria-expanded={!isConfigCollapsed}
                aria-controls="config-form"
              >
                <button 
                  type="button" 
                  className={`collapse-toggle ${isConfigCollapsed ? 'collapsed' : ''}`}
                  aria-label={isConfigCollapsed ? 'Expand configuration' : 'Collapse configuration'}
                >
                  <span>▼</span>
                </button>
                <h2>Configure Processing</h2>
              </div>
              {!isConfigCollapsed && (
                <div id="config-form">
                  <SplitAndClassifyRequestForm 
                    ref={formRef}
                    formData={formData}
                    onFormDataChange={handleFormDataChange}
                    onSubmit={(requestData, authToken) => handleFormSubmit(requestData, authToken)}
                    disabled={isProcessing}
                    onValidationChange={handleValidationChange}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="right-panel">
            {step === ProcessingStep.UPLOADING && (
              <div className="status-message">
                <h3>Uploading file...</h3>
                <div className="loading-spinner"></div>
              </div>
            )}

            {step === ProcessingStep.PROCESSING && (
              <div className="status-message">
                <h3>Processing document...</h3>
                <p>This may take a few minutes depending on document size and complexity.</p>
                <div className="loading-spinner"></div>
              </div>
            )}

            {step === ProcessingStep.ERROR && (
              <div className="error-message">
                <h3>Error</h3>
                <p>{error}</p>
              </div>
            )}

            {step === ProcessingStep.COMPLETED && results && (
              <div className="results-container">
                <SplitAndClassifyResults results={results} onDownload={handleDownload} />
              </div>
            )}

            {selectedFile && !results && step === ProcessingStep.FILE_SELECTION && (
              <div className="placeholder-message">
                <h3>{uploadSucceeded ? 'Ready to process' : 'File selected, upload in progress or failed'}</h3>
                <p>{uploadSucceeded ? 'Configure your settings and click "Process Document" to begin.' : 'Please wait for upload to complete or try uploading again.'}</p>
              </div>
            )}

            {!selectedFile && step === ProcessingStep.FILE_SELECTION && (
              <div className="placeholder-message">
                <h3>Upload a PDF to get started</h3>
                <p>You can configure your settings now, but you'll need to upload a PDF file before processing.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
